import type { HistoryEntry } from '@ctcells/contract'
import { t } from '@ctcells/i18n'

import { orchestrator } from '../clients.ts'
import { ApiError } from '../errors.ts'
import { keys } from '../keys.ts'
import { normalizeList } from '../page.ts'
import { createQuery } from '../query.ts'
import { EVENT_LEDGERS, mergeHistoryDesc } from './admin.ts'

export const BATCH_EVENT_PAGE_SIZE = 20
export type BatchEventOffsets = Readonly<Record<(typeof EVENT_LEDGERS)[number], number>>
export const INITIAL_EVENT_OFFSETS: BatchEventOffsets = { event: 0, batch_record: 0 }

interface BatchEventPageArgs {
  batchId: string
  to: string
  offsets: BatchEventOffsets
}

export async function fetchBatchEventPage({ batchId, to, offsets }: BatchEventPageArgs) {
  const pages = await Promise.all(
    EVENT_LEDGERS.map(async (ledger) => {
      const query = {
        batch_id: batchId,
        event_type: ledger,
        to,
        limit: BATCH_EVENT_PAGE_SIZE,
        offset: offsets[ledger],
      }
      const page = normalizeList<HistoryEntry>(
        await orchestrator.request({ method: 'GET', path: '/history', query }),
        query,
      )
      if (page.total_count === null || !Number.isInteger(page.total_count) || page.total_count < 0)
        throw new ApiError({ status: 0, message: t('error.eventTotalUnknown') })
      return { ...page, total_count: page.total_count }
    }),
  )
  const items = mergeHistoryDesc(pages.map((page) => page.items)).slice(0, BATCH_EVENT_PAGE_SIZE)
  const nextOffsets = { ...offsets }
  for (const ledger of EVENT_LEDGERS)
    nextOffsets[ledger] += items.filter((row) => row.event_type === ledger).length
  return { items, total: pages.reduce((total, page) => total + page.total_count, 0), nextOffsets }
}

export const useBatchEventPage = createQuery({
  key: (args: BatchEventPageArgs) =>
    keys.history.list({ scope: 'batch-event-page-events-only', ...args }),
  fetch: fetchBatchEventPage,
  enabled: ({ batchId }) => batchId !== '',
})
